import React, { useState, useEffect } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, Badge, Modal, ConfirmDialog, showGlobalToast } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import InventoryComparePage from './InventoryCompare';
import SyncLog from './SyncLog';
import ThirdPartyFeeds from './ThirdPartyFeeds';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

function ShopifyAccountModal({ account, onClose, onSaved }) {
  const isNew = !account?.id;
  const [form, setForm] = useState({
    name: account?.name || '',
    storeUrl: account?.store_url || '',
    authMethod: account?.auth_method || 'credentials',
    accessToken: '',
    clientId: account?.client_id || '',
    clientSecret: '',
    locationId: account?.location_id || '',
    syncIntervalMins: account?.sync_interval_mins || 15,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isNew) {
        await api.post('/api/shopify-accounts', form);
      } else {
        await api.patch(`/api/shopify-accounts/${account.id}`, form);
      }
      onSaved(); onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Add Shopify Store' : `Edit — ${account.name}`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="shopify-acct-form" type="submit">{saving ? 'Saving…' : isNew ? 'Add store' : 'Save'}</button></>}>
      <form id="shopify-acct-form" onSubmit={save}>
        <div className="form-group"><label>Store name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Retail Store" /></div>
        <div className="form-group"><label>Store URL *</label><input required value={form.storeUrl} onChange={e => set('storeUrl', e.target.value)} placeholder="yourstore.myshopify.com" /></div>
        <div className="form-group"><label>Authentication method</label>
          <select value={form.authMethod} onChange={e => set('authMethod', e.target.value)}>
            <option value="credentials">Client ID + Secret (Dev Dashboard)</option>
            <option value="token">Access token (legacy)</option>
          </select>
        </div>
        {form.authMethod === 'credentials' ? (
          <>
            <div className="form-group"><label>Client ID</label><input value={form.clientId} onChange={e => set('clientId', e.target.value)} /></div>
            <div className="form-group"><label>Client secret {!isNew && <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>(leave blank to keep existing)</span>}</label><input type="text" value={form.clientSecret} onChange={e => set('clientSecret', e.target.value)} placeholder={!isNew ? '(enter new secret to update)' : 'Paste client secret'} /></div>
          </>
        ) : (
          <div className="form-group"><label>Access token {!isNew && <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>(leave blank to keep existing)</span>}</label><input type="password" value={form.accessToken} onChange={e => set('accessToken', e.target.value)} placeholder="shpat_xxxxxxxxxxxx" /></div>
        )}
        <div className="form-group"><label>Location ID</label><input value={form.locationId} onChange={e => set('locationId', e.target.value)} placeholder="Required for inventory push" /></div>
        <div className="form-group"><label>Sync interval</label>
          <select value={form.syncIntervalMins} onChange={e => set('syncIntervalMins', parseInt(e.target.value))}>
            <option value={5}>Every 5 minutes</option>
            <option value={15}>Every 15 minutes</option>
            <option value={60}>Every hour</option>
          </select>
        </div>
      </form>
    </Modal>
  );
}

function ShopifyAccountCard({ account, onEdit, onDelete, onRefresh, onCompare }) {
  const [loading, setLoading] = useState({});
  const [messages, setMessages] = useState({});
  const [pushEnabled, setPushEnabled] = useState(account.push_inventory_enabled !== false);

  useEffect(() => {
    setPushEnabled(account.push_inventory_enabled !== false);
  }, [account.push_inventory_enabled]);

  const togglePush = async () => {
    const newVal = !pushEnabled;
    setPushEnabled(newVal);
    try {
      const res = await api.patch(`/api/shopify-accounts/${account.id}`, { pushInventoryEnabled: newVal });
      console.log('[togglePush] sent:', newVal, 'response:', res.data);
    } catch (err) {
      console.error('[togglePush] error:', err.response?.data || err.message);
      setPushEnabled(!newVal);
      alert(errMsg(err));
    }
  };

  const run = async (action, label, endpoint, confirm) => {
    if (confirm && !window.confirm(confirm)) return;
    setLoading(l => ({ ...l, [action]: true }));
    setMessages(m => ({ ...m, [action]: null }));
    try {
      const { data } = await api.post(endpoint);
      setMessages(m => ({ ...m, [action]: data.message || label + ' complete' }));
      onRefresh();
    } catch (err) {
      setMessages(m => ({ ...m, [action]: errMsg(err) }));
    } finally {
      setLoading(l => ({ ...l, [action]: false }));
    }
  };

  const statusColor = account.last_sync_status === 'success' ? '#4ade80'
    : account.last_sync_status === 'error' ? '#ef4444' : '#94a3b8';

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <i className="ti ti-brand-shopify" style={{ fontSize: 22, color: '#95BF47' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 15 }}>{account.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {account.store_url}
            {account.last_sync_at && ` · Last sync: ${fmt.dateTime(account.last_sync_at)}`}
          </div>
        </div>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
        <Badge status={account.active ? 'active' : 'cancelled'} label={account.active ? 'Active' : 'Inactive'} />
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', userSelect:'none', margin:0 }}
          title="Enable or disable inventory quantity pushes to this store">
          <input type="checkbox" checked={pushEnabled} onChange={togglePush} style={{ width:'auto' }} />
          Push inventory
        </label>
        <button className="btn btn-sm" onClick={() => onCompare(account)}><i className="ti ti-scale" /> Compare inventory</button>
        <button className="btn btn-sm" onClick={() => onEdit(account)}><i className="ti ti-pencil" /></button>
        <button className="btn btn-sm btn-danger" onClick={() => onDelete(account)}><i className="ti ti-trash" /></button>
      </div>

      {/* Success/error messages */}
      {Object.entries(messages).map(([k, msg]) => msg && (
        <div key={k} className={`alert ${msg.includes('error') || msg.includes('fail') ? 'alert-danger' : 'alert-success'}`} style={{ marginBottom: 8, padding: '6px 10px' }}>
          <i className={`ti ${msg.includes('error') || msg.includes('fail') ? 'ti-alert-circle' : 'ti-check'}`} />
          <span style={{ fontSize: 12 }}>{msg}</span>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-sm btn-primary" disabled={loading.sync}
          onClick={() => run('sync', 'Sync', `/api/integrations/shopify/${account.id}/sync`)}>
          <i className="ti ti-refresh" /> {loading.sync ? 'Syncing…' : 'Sync now'}
        </button>
        <button className="btn btn-sm" disabled={loading.syncOrdersFull}
          onClick={() => run('syncOrdersFull', 'Full order sync', `/api/integrations/shopify/sync-orders-full`,
            'This will resync ALL orders from Shopify and update their statuses. May take several minutes. Continue?')}>
          <i className="ti ti-refresh-alert" /> {loading.syncOrdersFull ? 'Syncing…' : 'Sync all order statuses'}
        </button>
        <button className="btn btn-sm" disabled={loading.pull}
          onClick={() => run('pull', 'Pull inventory', `/api/integrations/shopify/${account.id}/pull-inventory`,
            'This will overwrite StockForge warehouse quantities with current Shopify inventory levels. Continue?')}>
          <i className="ti ti-package-import" /> {loading.pull ? 'Pulling…' : 'Pull inventory'}
        </button>
        <button className="btn btn-sm" disabled={loading.syncByName}
          onClick={() => run('syncByName', 'Import by name', `/api/integrations/shopify/${account.id}/sync-by-name`,
            'Import products from Shopify using product name as the key (no SKU required). New products will be created, existing ones updated. Continue?')}>
          <i className="ti ti-tag" /> {loading.syncByName ? 'Importing…' : 'Import products by name'}
        </button>
        <button className="btn btn-sm" disabled={loading.backfill}
          onClick={() => run('backfill', 'Backfill', `/api/integrations/shopify/${account.id}/backfill?days=90`,
            'Import last 90 days of Shopify sales history for this store?')}>
          <i className="ti ti-history" /> {loading.backfill ? 'Importing…' : 'Backfill 90-day history'}
        </button>
        <button className="btn btn-sm" disabled={loading.syncImages}
          onClick={() => run('syncImages', 'Sync Images', `/api/integrations/shopify/${account.id}/sync-images`,
            'Download all product images from Shopify for this store? This may take several minutes.')}>
          <i className="ti ti-photo" /> {loading.syncImages ? 'Syncing images…' : 'Sync product images'}
        </button>
        <button className="btn btn-sm" disabled={loading.pullDescriptions}
          onClick={() => run('pullDescriptions', 'Pull Descriptions', `/api/integrations/shopify/${account.id}/pull-descriptions`,
            'Pull product descriptions (HTML) from Shopify for this store? This will overwrite existing descriptions.')}>
          <i className="ti ti-file-description" /> {loading.pullDescriptions ? 'Pulling…' : 'Pull descriptions'}
        </button>
      </div>
    </div>
  );
}

function PendingPushesPanel() {
  const { data, loading, refetch } = useFetch('/api/integrations/shopify/pending-pushes');
  const [retrying, setRetrying] = useState(false);
  const [clearing, setClearing] = useState(false);

  const retryAll = async () => {
    setRetrying(true);
    try {
      const { data: r } = await api.post('/api/integrations/shopify/retry-pushes');
      showGlobalToast(r.message);
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setRetrying(false); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all pending pushes without executing them? They will not be retried.')) return;
    setClearing(true);
    try {
      const { data: r } = await api.delete('/api/integrations/shopify/pending-pushes');
      showGlobalToast(r.message);
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setClearing(false); }
  };

  if (loading) return null;
  const pending = data || [];
  if (!pending.length) return null;

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--color-warning)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 22, color: 'var(--color-warning)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500 }}>Pending inventory pushes</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {pending.length} product{pending.length !== 1 ? 's' : ''} failed to sync to Shopify — will retry automatically every 10 minutes
          </div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={retryAll} disabled={retrying || clearing}>
          <i className="ti ti-refresh" /> {retrying ? 'Retrying…' : 'Retry now'}
        </button>
        <button className="btn btn-sm btn-danger" onClick={clearAll} disabled={retrying || clearing}>
          <i className="ti ti-trash" /> {clearing ? 'Clearing…' : 'Clear all'}
        </button>
      </div>
      <table style={{ width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>SKU</th>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Product</th>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Store</th>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Type</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>Value to sync</th>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Last error</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>Attempts</th>
          </tr>
        </thead>
        <tbody>
          {pending.map(p => (
            <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{p.sku}</td>
              <td style={{ padding: '6px 8px' }}>{p.product_name}</td>
              <td style={{ padding: '6px 8px' }}>{p.store_name}</td>
              <td style={{ padding: '6px 8px' }}>{p.push_type === 'sku' ? 'SKU' : 'Inventory'}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: p.push_type === 'sku' ? 'monospace' : undefined }}>
                {p.push_type === 'sku' ? p.sku_value : p.quantity}
              </td>
              <td style={{ padding: '6px 8px', color: 'var(--color-danger)' }}>{p.last_error}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.attempts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AmazonCard({ isAdmin, onSyncDone }) {
  const { data: cfg, refetch: refetchCfg } = useFetch('/api/integrations/amazon/config');
  const { data: status } = useFetch('/api/integrations/status');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (cfg && !form) setForm({
      refreshToken: cfg.refreshToken || '',
      clientId: cfg.clientId || '',
      clientSecret: cfg.clientSecret || '',
      marketplaceId: cfg.marketplaceId || '',
      sellerId: cfg.sellerId || '',
    });
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/integrations/amazon/config', form);
      showGlobalToast('Amazon credentials saved');
      refetchCfg();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post('/api/integrations/amazon/sync');
      showGlobalToast('Amazon sync started');
      onSyncDone?.();
    } catch (err) { alert(errMsg(err)); }
    finally { setSyncing(false); }
  };

  const connected = cfg?.hasCredentials;

  return (
    <>
      <div style={{ marginBottom:8, marginTop:8, fontWeight:500, fontSize:13, color:'var(--text-secondary)' }}>
        <i className="ti ti-brand-amazon" style={{ marginRight:6 }} />
        Amazon Seller Central
      </div>
      <div className="card" style={{ marginBottom:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
          <i className="ti ti-brand-amazon" style={{ fontSize:22, color:'#FF9900' }} />
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:500 }}>Amazon SP-API</div>
            <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>
              {connected ? 'Credentials configured' : 'Not configured — enter credentials below'}
            </div>
          </div>
          <Badge status={connected ? 'active' : 'cancelled'} label={connected ? 'Connected' : 'Not connected'} />
          {connected && (
            <button className="btn btn-sm btn-primary" onClick={sync} disabled={syncing}>
              <i className="ti ti-refresh" /> {syncing ? 'Starting…' : 'Sync now'}
            </button>
          )}
        </div>
        {isAdmin && form && (
          <details style={{ borderTop:'1px solid var(--border)', paddingTop:12, marginTop:4 }}>
            <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', userSelect:'none', marginBottom:12 }}>
              <i className="ti ti-key" style={{ marginRight:6 }} />
              SP-API Credentials
            </summary>
            <div className="form-row">
              <div className="form-group">
                <label>Client ID (LWA)</label>
                <input value={form.clientId} onChange={e => set('clientId', e.target.value)} placeholder="amzn1.application-oa2-client...." />
              </div>
              <div className="form-group">
                <label>Client Secret (LWA)</label>
                <input type="password" value={form.clientSecret} onChange={e => set('clientSecret', e.target.value)} placeholder={cfg?.clientSecret ? 'Already set — enter to change' : 'Client secret'} />
              </div>
            </div>
            <div className="form-group">
              <label>Refresh Token</label>
              <input type="password" value={form.refreshToken} onChange={e => set('refreshToken', e.target.value)} placeholder={cfg?.refreshToken ? 'Already set — enter to change' : 'Atzr|...'} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Marketplace ID</label>
                <input value={form.marketplaceId} onChange={e => set('marketplaceId', e.target.value)} placeholder="ATVPDKIKX0DER (US)" />
              </div>
              <div className="form-group">
                <label>Seller ID</label>
                <input value={form.sellerId} onChange={e => set('sellerId', e.target.value)} placeholder="A1B2C3D4E5F6G7" />
              </div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
              <i className="ti ti-device-floppy" /> {saving ? 'Saving…' : 'Save credentials'}
            </button>
          </details>
        )}
      </div>
    </>
  );
}

export default function Integrations() {
  const { data, loading, error, refetch } = useFetch('/api/integrations/status');
  const { isAdmin } = useAuth();
  const [accountModal, setAccountModal] = useState(null); // null | 'new' | account obj
  const [compareAccount, setCompareAccount] = useState(null);
  const [showSyncLog, setShowSyncLog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deleteAccount = async (account) => {
    try { await api.delete(`/api/shopify-accounts/${account.id}`); refetch(); }
    catch (err) { alert(errMsg(err)); }
    finally { setDeleteConfirm(null); }
  };

  const connectQB = async () => {
    try { const { data: r } = await api.get('/api/integrations/quickbooks/connect'); window.location.href = r.authUrl; }
    catch (err) { alert(errMsg(err)); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      {accountModal && (
        <ShopifyAccountModal
          account={accountModal === 'new' ? null : accountModal}
          onClose={() => setAccountModal(null)}
          onSaved={refetch}
        />
      )}
      {deleteConfirm && (
        <ConfirmDialog danger
          message={`Delete store "${deleteConfirm.name}"? All product links for this store will also be removed.`}
          onConfirm={() => deleteAccount(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {compareAccount && (
        <InventoryComparePage account={compareAccount} onClose={() => setCompareAccount(null)} />
      )}
      {showSyncLog && (
        <SyncLog onClose={() => setShowSyncLog(false)} />
      )}
      {!compareAccount && !showSyncLog && <>
      <div className="page-header">
        <h1>Integrations</h1>
        <div className="page-actions">
          <button className="btn btn-sm" onClick={() => setShowSyncLog(true)}>
            <i className="ti ti-list" /> Sync log
          </button>
          <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /> Refresh</button>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setAccountModal('new')}>
              <i className="ti ti-plus" /> Add Shopify store
            </button>
          )}
        </div>
      </div>

      {/* Shopify stores */}
      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: 'var(--text-secondary)' }}>
        <i className="ti ti-brand-shopify" style={{ marginRight: 6, color: '#95BF47' }} />
        Shopify Stores
      </div>
      {(data?.shopify || []).length === 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="ti ti-brand-shopify" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>No Shopify stores connected</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Add a store to start syncing orders and inventory</div>
            </div>
            {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setAccountModal('new')}><i className="ti ti-plus" /> Add store</button>}
          </div>
        </div>
      )}
      {!compareAccount && (data?.shopify || []).map(account => (
        <ShopifyAccountCard
          key={account.id}
          account={account}
          onEdit={a => setAccountModal(a)}
          onDelete={a => setDeleteConfirm(a)}
          onRefresh={refetch}
          onCompare={a => setCompareAccount(a)}
        />
      ))}

      {isAdmin && <PendingPushesPanel />}

      {/* Amazon */}
      <AmazonCard isAdmin={isAdmin} onSyncDone={refetch} />

      {/* QuickBooks */}
      <div style={{ marginBottom: 8, marginTop: 8, fontWeight: 500, fontSize: 13, color: 'var(--text-secondary)' }}>
        <i className="ti ti-calculator" style={{ marginRight: 6 }} />
        QuickBooks Online
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: data?.quickbooks?.connected ? 0 : 10 }}>
          <i className="ti ti-calculator" style={{ fontSize: 22, color: '#2CA01C' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>QuickBooks Online</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {data?.quickbooks?.connected ? 'Connected — purchase orders can be pushed as Bills' : 'Not connected'}
            </div>
          </div>
          <Badge status={data?.quickbooks?.connected ? 'active' : 'cancelled'} label={data?.quickbooks?.connected ? 'Connected' : 'Not connected'} />
        </div>
        {!data?.quickbooks?.connected && isAdmin && (
          <button className="btn btn-sm btn-primary" onClick={connectQB}>Connect QuickBooks</button>
        )}
      </div>
      {/* Third Party Feeds */}
      <ThirdPartyFeeds isAdmin={isAdmin} />

      </>}
    </div>
  );
}
